class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const innerHtml = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

      if (innerHtml) {
        sectionElement.innerHTML = innerHtml;
      }
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
     {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner > .drawer__header'
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items'
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner > .drawer__header'
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items'
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class TieredProgressBar extends HTMLElement {
  constructor() {
    super();

    this.setProperties();

    if (this.tiers.length > 0 && this.currentTier != null) {
      this.render();
    }
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source == 'product-form') {
         setTimeout(() => {
          this.onCartUpdate();
        }, 300);
      }
      else {
        this.onCartUpdate();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  debug() {
    console.log('*** DEBUG ***')
    console.log("Settings: ", this.settings);
    console.log("Current tier: ", this.currentTier);
    console.log("Last tier: ", this.lastTier);
    console.log("Is complete: ", this.isComplete);
    console.log("Cart price: ", this.cartPrice);
    console.log("Max price: ", this.maxPrice);
    console.log("Line items", this.lineItems);
    console.log('*** END DEBUG ***')
  }

  setProperties() {
    const cartDrawerItems = document.getElementById('CartDrawer-CartItems');
    this.cartPrice = parseFloat((cartDrawerItems?.dataset.cartPrice ? parseFloat(cartDrawerItems.dataset.cartPrice) : 0).toString().replace('.', "").replace(',', ""));
    this.settings = this.dataset.settings ? JSON.parse(this.dataset.settings) : {};
    this.tiers = this.settings.tiers;
    this.lastTier = this.tiers[this.tiers.length - 1];
    this.currentTier = this.tiers.find(tier => this.cartPrice < parseFloat(tier.value)) ?? this.lastTier;
    this.maxPrice = this.lastTier.value ? parseFloat(this.lastTier.value) : 0;
    this.isComplete = this.currentTier === this.lastTier && this.cartPrice >= this.maxPrice;

    this.lineItems = Array.from(document.querySelectorAll('.cart-items .cart-item')).map((item) => { 
      const properties = item.dataset.properties != null ? JSON.parse(item.dataset.properties) : [];

      return {
        sku: item.dataset.sku,
        productId: item.dataset.productId,
        variantId: item.dataset.variantId,
        quantity: item.dataset.quantity ? parseInt(item.dataset.quantity) : 0,
        properties: properties.map((property) => { 
          return {
            name: property[0],
            value: property[1]
          }
        })
      }
    });
  }

  render() {
    this.classList.remove('hide');

    try {
      const progressBarFill = this.querySelector('.tbp__progress-bar-fill');
      const progressBarText = this.querySelector('.tbp__progress-text');

      const progress = this.cartPrice / this.maxPrice;
      const progressPercentage = Math.min(100, Math.round(progress * 100));

      progressBarFill.style.width = `${progressPercentage}%`;

      if (this.isComplete) {
        progressBarText.innerText = this.currentTier.reachedText;
      }
      else {
        const remainingAmount = this.currentTier.value - this.cartPrice;
        const remainingAmountFormatted = this.formatMoney(remainingAmount, Shopify.currency.active);
        
        progressBarText.innerText = this.currentTier.text
          .replace('{{ remaining_amount }}', remainingAmountFormatted)
          .replace('{{remaining_amount}}', remainingAmountFormatted);
      }

      if (this.tiers.length > 1) {
        this.tiers.forEach((tier, i) => { 
          const node = this.querySelector(`.tier-${i + 1}`);
          const isComplete = this.cartPrice >= tier.value;

          if (isComplete) {
            node.classList.add('is-complete');
          }
          else {
            node.classList.remove('is-complete');
          }
        });
      }
    }
    catch (err) {
      console.error(err);
    }
  }

  formatMoney(amount, currencyCode) {
    const {locales, maximumFractionDigits} =
      this.getNumberFormatSettings(currencyCode);

    return new Intl.NumberFormat(locales, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits,
    }).format(amount);
  }

  getNumberFormatSettings(currencyCode) {
    switch (currencyCode) {
      case 'EUR':
        return {
          locales: undefined,
          maximumFractionDigits: 2,
        };
      case 'USD':
        return {
          locales: 'en-US',
          maximumFractionDigits: 1,
        };
      case 'DKK':
        return {
          locales: 'da-DK',
          maximumFractionDigits: 0,
        };
      case 'GBP':
        return {
          locales: 'en-GB',
          maximumFractionDigits: 1,
        };
      case 'SEK':
        return {
          locales: 'sv-SE',
          maximumFractionDigits: 0,
        };
      case 'NOK':
        return {
          locales: 'nb-NO',
          maximumFractionDigits: 0,
        };
      default:
        return {
          locales: 'en-US',
          maximumFractionDigits: 1,
        };
    }
  }

  onCartUpdate() {
    this.setProperties();
    this.render();

    this.tiers.forEach((tier, i) => {
      const isComplete = this.cartPrice >= tier.value;

      if (isComplete && tier.type === 'free_product') {
        const lineItemInCart = this.lineItems.find(item => item.variantId === tier.productVariantId && item.properties.find(x => x.name === '_source' && x.value === 'Cart Slider'));

        if (!lineItemInCart) {
          this.addLineItem(Number(tier.productVariantId));
        }
      }
      else if (!isComplete && tier.type === 'free_product') {
        const lineItemInCart = this.lineItems.find(item => item.variantId === tier.productVariantId && item.properties.find(x => x.name === '_source' && x.value === 'Cart Slider'));

        if (lineItemInCart) {
          const index = this.lineItems.findIndex(item => item.id === lineItemInCart.id);
          this.removeLineItem(index + 1);
        }
      }
    });
  }

  addLineItem(variantId) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('properties[_source]', "Cart Slider");
    formData.append('properties[_attribution]', "Free Gift with Purchase");

    const cartDrawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-drawer-items');
    formData.append('sections', cartItems.getSectionsToRender().map((section) => section.section));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((state) => {
        cartDrawer.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          const innerHtml = cartDrawer.getSectionInnerHTML(state.sections[section.section], section.selector);

          if (innerHtml) {
            elementToReplace.innerHTML = innerHtml;  
          }
        }));
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
      });
  }

  removeLineItem(index) {
    const cartItems = document.querySelector('cart-drawer-items');
    cartItems.updateQuantity(index, 0);
  }
}

customElements.define('tiered-progress-bar', TieredProgressBar);
